import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@marriott/mi-ui-library';

import { StyledTabNavigation, StyledTabbedWrapper } from './Tabs.styles';
import { TabNavigationListProps } from './Tabs.types';
import { POLARIS_CONSTANTS } from '../../utils/constants';

export const TabNavigation: React.FC<TabNavigationListProps> = props => {
  const { tabNavigationList, selectedTab, setSelectedTab, children, pageTitle } = props;
  const navigationRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const { POLARIS_TAB_TITLE_ID, SEARCH_FOCUS_EVENT, SEARCH_BLUR_EVENT } = POLARIS_CONSTANTS;

  const selectedTabTitle = tabNavigationList.find(tab => tab.tabValue === selectedTab)?.tabTitle;

  useEffect(() => {
    const handleInitialCheck = () => {
      requestAnimationFrame(checkOverflow);
    };
    handleInitialCheck();
    window.addEventListener('resize', checkOverflow);
    navigationRef.current?.addEventListener('scroll', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);
  const checkOverflow = () => {
    if (!navigationRef.current || !containerRef.current) return;
    const { scrollWidth, clientWidth, scrollLeft } = navigationRef.current;
    setShowLeftArrow(scrollLeft > 0);
    /*
    When scroll is at very right end.
    Math.abs() < 2 to allows for tiny rounding or fractional differences
    that can occur across browsers or OS.
   */
    const isAtRightEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) < 2;
    const showRightArrow = isAtRightEnd ? false : scrollLeft + clientWidth < scrollWidth;
    setShowRightArrow(showRightArrow);
  };
  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    if (navigationRef.current) {
      navigationRef.current.addEventListener('scroll', checkOverflow);
    }
    return () => {
      window.removeEventListener('resize', checkOverflow);
      if (navigationRef.current) {
        navigationRef.current.removeEventListener('scroll', checkOverflow);
      }
    };
  }, []);

  useEffect(() => {
    const titleEl = document.getElementById(POLARIS_TAB_TITLE_ID);
    if (!titleEl) return;

    const onSearchFocus = () => {
      titleEl.setAttribute('aria-hidden', 'false');
    };
    const onSearchBlur = () => {
      titleEl.setAttribute('aria-hidden', 'true');
    };
    window.addEventListener(SEARCH_FOCUS_EVENT, onSearchFocus);
    window.addEventListener(SEARCH_BLUR_EVENT, onSearchBlur);

    titleEl.setAttribute('aria-hidden', 'true');
    return () => {
      window.removeEventListener(SEARCH_FOCUS_EVENT, onSearchFocus);
      window.removeEventListener(SEARCH_BLUR_EVENT, onSearchBlur);
    };
  }, [POLARIS_TAB_TITLE_ID]);

  const handleScroll = (direction: string) => {
    if (!navigationRef.current) return;

    const container = navigationRef.current;
    const currentScroll = container.scrollLeft;
    // to add padding of the clientWidth to the scroll amount for right scroll and subtract for left scroll
    const sidePaddingAmount = 18;
    // Find all tab elements
    const tabs = Object.values(tabRefs.current).filter((tab): tab is HTMLButtonElement => !!tab);
    const visibleRight = currentScroll + container.clientWidth;
    if (tabs.length === 0) return;

    if (direction === 'right') {
      // first tab that ends beyond the visible right edge (partially or fully hidden)
      const nextTab = tabs.find(tab => tab.offsetLeft + tab.offsetWidth + sidePaddingAmount > visibleRight);
      if (nextTab) {
        // align nextTab's RIGHT edge with the container's right edge
        const desired = nextTab.offsetLeft + nextTab.offsetWidth - container.clientWidth;
        // clamp between 0 and max scrollLeft
        const maxScroll = container.scrollWidth - container.clientWidth;
        const target = Math.max(0, Math.min(desired, maxScroll));
        container.scrollTo({ left: target + sidePaddingAmount, behavior: 'smooth' });
      } else {
        // no next tab (we're at/near the end) -> ensure we scroll to the end
        container.scrollTo({ left: container.scrollWidth - container.clientWidth, behavior: 'smooth' });
      }
    } else {
      // left: find the first tab whose left is before currentScroll (i.e., hidden on the left)
      const prevTab = [...tabs].reverse().find(tab => tab.offsetLeft < currentScroll);
      if (prevTab) {
        // align that tab's LEFT edge with the container's left edge
        const scrollAmountLeft = prevTab.offsetLeft - sidePaddingAmount;
        const target = Math.max(0, scrollAmountLeft);

        container.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        // already at the start
        container.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }
  };
  // - If selectedTab equals a tabValue, parent uses value-mode (TabbedLayout)
  // - Otherwise assume title-mode (DynamicTabbedLayout)
  const isValueMode = tabNavigationList.some(t => t.tabValue === selectedTab);
  return (
    <StyledTabbedWrapper
      className={clsx('container tabs-wrapper', {
        'show-left-arrow': showLeftArrow,
        'show-right-arrow': showRightArrow,
      })}
      ref={containerRef}
    >
      <span id={POLARIS_TAB_TITLE_ID} className="sr-only">
        {pageTitle}
      </span>
      <p aria-hidden="true" className="tab-banner-title t-title-m">
        <span>{pageTitle}</span>
      </p>
      {showLeftArrow && (
        <div className="tab-arrow left" data-testid="left-arrow" onClick={() => handleScroll('left')}>
          <span className="icon-arrow-left" data-id="left-arrow"></span>
        </div>
      )}
      {showRightArrow && (
        <div className="tab-arrow right" data-testid="right-arrow" onClick={() => handleScroll('right')}>
          <span className="icon-arrow-right" data-id="right-arrow"></span>
        </div>
      )}
      <StyledTabNavigation
        data-testid="tab-navigation"
        className={clsx('tab-navigation', {
          'tab-gradient-left': showLeftArrow && !showRightArrow,
          'tab-gradient-right': !showLeftArrow && showRightArrow,
          'tab-gradient-dual': showLeftArrow && showRightArrow,
        })}
        role="tablist"
        ref={navigationRef}
      >
        {tabNavigationList.map((tab, index) => {
          const { tabTitle, tabValue, tabIcon, tabTrackingDescription, customTabClickTrackValue } = tab;
          const isActive = isValueMode ? selectedTab === tabValue : selectedTab === tabTitle;
          const tabKey = tabValue || `tab-${index}`;
          return (
            <Button
              key={tabKey}
              ref={(el: HTMLButtonElement | null) => {
                tabRefs.current[tabKey] = el;
              }}
              id={tabValue}
              isLink={true}
              href={isValueMode ? `#${encodeURIComponent(tabValue)}` : `?tab=${encodeURIComponent(tabTitle)}`}
              custom_click_track_value={customTabClickTrackValue || tabTrackingDescription}
              ariaRole="tab"
              callback={e => {
                e.preventDefault(); // prevent default anchor navigation
                if (!isActive) setSelectedTab?.(isValueMode ? tabValue : tabTitle);
              }}
              buttonCopy={tabTitle}
              isTextBeforeChildren={false}
              aria-controls={`${isValueMode ? tabValue : tabTitle}-panel`}
              aria-selected={isActive ? 'true' : 'false'}
              className={clsx('tab-item m-icon-text', isActive && 'is-active')}
            >
              <span className={clsx('icon-m', tabIcon)}></span>
            </Button>
          );
        })}
      </StyledTabNavigation>
      {children}
    </StyledTabbedWrapper>
  );
};
